import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useOutletContext, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";
import {
  updateHomework,
  getAssignedStudents,
  getHomeworkById,
} from "../../../helper/apicalls/homework";
import "../../../styles/teacher/AssignHomework.scss";
import CommonTable from "../../common/commonTable";
import Pagination from "../../common/Pagination";

const AssignHomework = () => {
  const [searchParams] = useSearchParams();
  const homeworkId = searchParams.get("homework");
  const batchId = searchParams.get("batch");
  const { setHeaderName, setIsShowSearch } = useOutletContext();
  const {
    key: courseKey,
    subkey: subjectKey,
    topicKey,
    subtopicKey,
  } = useParams();

  const authToken = Cookies.get("institution_token");

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [homeworkName, setHomeworkName] = useState("");
  const searchDebounceRef = useRef(null);

  const fetchStudents = async (
    currentPage = page,
    currentLimit = limit,
    currentSearch = searchTerm,
  ) => {
    setLoading(true);
    setError("");
    try {
      const params = { page: currentPage, limit: currentLimit };
      if (currentSearch.trim()) params.search = currentSearch.trim();
      const level = subtopicKey
        ? "subtopic"
        : topicKey
          ? "topic"
          : subjectKey
            ? "subject"
            : courseKey
              ? "course"
              : "course";
      params.level = level;
      if (homeworkId) params.id = homeworkId;
      const res = await getAssignedStudents(params);
      if (res && Array.isArray(res.data)) {
        setStudents(res.data);
        setTotalPages(Number(res.total_pages) || 1);
      } else {
        setStudents(Array.isArray(res) ? res : []);
        setTotalPages(1);
      }
    } catch (e) {
      setError("Failed to load students");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAssigned = async (rowKey, row) => {
    if (!homeworkId) {
      setError("Missing homework reference");
      return;
    }
    const removeId = row?.id || row?._id || rowKey;
    if (!removeId) return;
    setLoading(true);
    setError("");
    try {
      const alreadyAssigned = (students || [])
        .filter((s) => s?.homework_assign)
        .map((s) => s?.id || s?._id)
        .filter(Boolean);
      const nextUsers = alreadyAssigned.filter((uid) => uid !== removeId);
      await updateHomework({ id: homeworkId, users: nextUsers });
      setSelectedIds((prev) => prev.filter((id) => id !== removeId));
      await fetchStudents(page, limit, searchTerm);
    } catch (e) {
      setError("Failed to remove student from homework");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHeaderName && setHeaderName(homeworkName ? `Assign Homework - ${homeworkName}` : "Assign Homework");
    setIsShowSearch && setIsShowSearch(false);
  }, [setHeaderName, setIsShowSearch, homeworkName]);

  useEffect(() => {
    const fetchHomework = async () => {
      if (!homeworkId) return;
      try {
        const level = subtopicKey
          ? "subtopic"
          : topicKey
            ? "topic"
            : subjectKey
              ? "subject"
              : courseKey
                ? "course"
                : "course";
        const res = await getHomeworkById(homeworkId, level);
        if (res && res.name) {
          setHomeworkName(res.name);
        }
      } catch (err) {
        console.error("Failed to fetch homework details:", err);
      }
    };
    fetchHomework();
  }, [homeworkId, courseKey, subjectKey, topicKey, subtopicKey]);

  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val || "");
      setPage(1);
    }, 300);
  };

  useEffect(() => {
    fetchStudents(page, limit, searchTerm);
  }, [batchId, searchTerm, page, limit]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
    });
  };

  const eligibleIds = (students || [])
    .filter((s) => !s?.homework_assign)
    .map((s) => s?.id || s?._id);
  const allSelected =
    eligibleIds.length > 0 &&
    eligibleIds.every((id) => selectedIds.includes(id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !eligibleIds.includes(id)));
    } else {
      setSelectedIds((prev) =>
        Array.from(new Set([...(prev || []), ...eligibleIds])),
      );
    }
  };

  const handleAssign = async () => {
    if (!homeworkId) {
      setError("Missing homework reference");
      return;
    }
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      setError("Please select at least one student");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const alreadyAssigned = (students || [])
        .filter((s) => s?.homework_assign)
        .map((s) => s?.id || s?._id)
        .filter(Boolean);
      const merged = Array.from(
        new Set([...(alreadyAssigned || []), ...selectedIds]),
      );
      await updateHomework({ id: homeworkId, users: merged });
      setSelectedIds([]);
      await fetchStudents(page, limit, searchTerm);
    } catch (e) {
      setError("Failed to assign homework");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="assign-homework-container">
      <GlobalLoader visible={loading} text="Loading students..." />
      <div className="assign-homework-tab">
        {!loading && error && <EmptyPage content={error} />}
        {!loading && !error && (
          <>
            <CommonTable
              data={students}
              emptyText="No students Available"
              columns={[
                {
                  field: "name",
                  label: "Name",
                  render: (_v, r) =>
                    r?.name ||
                    `${r?.first_name || ""} ${r?.last_name || ""}`.trim() ||
                    "Student",
                },
                {
                  field: "email",
                  label: "Email",
                  render: (_v, r) => r?.email || r?.username || "-",
                },
                {
                  field: "register_no",
                  label: "Register No.",
                  align: "center",
                  render: (v) => v || "-",
                },
              ]}
              getRowId={(r) => r?.id || r?._id}
              showActions
              showEditAction={false}
              actionLabelDelete={"Remove"}
              deleteMessage={"Remove this student from the homework?"}
              isConfirmDeleteOpen={isConfirmDeleteOpen}
              setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
              handleDelete={handleRemoveAssigned}
              isRowActionable={(row) => !!row?.homework_assign}
              showToolbar
              title={homeworkName ? `Students List - ${homeworkName}` : "Students List"}
              onSearchChange={handleSearchChange}
              toolbarRight={
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="assign-homework-header-btn btn-interactive"
                    onClick={toggleAll}
                  >
                    {allSelected ? "Unselect All" : "Select All"}
                  </button>
                  <button
                    type="button"
                    className="assign-homework-header-btn btn-interactive"
                    onClick={handleAssign}
                    disabled={
                      !Array.isArray(selectedIds) || selectedIds.length === 0
                    }
                    title={
                      !Array.isArray(selectedIds) || selectedIds.length === 0
                        ? "Select at least one student to assign"
                        : undefined
                    }
                  >
                    Assign
                  </button>
                </div>
              }
              isRowSelectable={(r) => !r?.homework_assign}
              selectedRowIds={selectedIds}
              onSelectionChange={setSelectedIds}
              multiSelect={true}
              maxBodyHeight={`calc(-350px + 100vh)`}
              fixedBodyRows={8}
            />
            <div className="student-pagination" style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <Pagination
                page={page}
                pageCount={totalPages}
                onChange={(p) => setPage(p)}
                perPage={limit}
                perPageOptions={[5, 10, 25, 100]}
                onPerPageChange={(pp) => {
                  setLimit(pp);
                  setPage(1);
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssignHomework;
